import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Crown, Home, Plane, ShieldCheck, Goal } from 'lucide-react';
import { premiumApi } from '../lib/api';

const RES_STYLE = {
  W: 'bg-[#00E676]/20 border-[#00E676]/50 text-[#00E676]',
  D: 'bg-slate-500/20 border-slate-500/50 text-slate-300',
  L: 'bg-[#FF1744]/20 border-[#FF1744]/50 text-[#FF1744]',
};

export const ClubAdvancedStats = ({ clubId, isItalian }) => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    premiumApi.advancedStats(clubId).then(r => setStats(r.data)).catch(() => setStats(null));
  }, [clubId]);

  if (!stats) return null;
  const resLabel = { W: 'V', D: 'N', L: 'P' };

  const Record = ({ icon: Icon, label, rec }) => (
    <div className="bg-[#050508] border border-white/10 p-3 flex items-center gap-3">
      <Icon size={18} className="text-[#FFD700]" />
      <div>
        <p className="text-[10px] text-slate-500 uppercase tracking-wider">{label}</p>
        <p className="font-mono text-sm text-white">
          <span className="text-[#00E676]">{rec.won}V</span> {rec.drawn}N <span className="text-[#FF1744]">{rec.lost}P</span>
        </p>
      </div>
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-[#0a0d15] border border-[#FFD700]/25 p-6 space-y-5"
      data-testid="club-advanced-stats"
    >
      <div className="flex items-center gap-2">
        <Crown size={18} className="text-[#FFD700]" />
        <h2 className="font-heading text-lg font-bold uppercase tracking-wider text-[#FFD700]">
          {isItalian ? 'Statistiche Avanzate' : 'Advanced Stats'}
        </h2>
        <span className="text-[10px] px-2 py-0.5 bg-[#FFD700]/10 border border-[#FFD700]/30 text-[#FFD700] rounded-full uppercase tracking-wider">Premium</span>
      </div>

      {/* Form */}
      <div>
        <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">{isItalian ? 'Forma (ultime 5)' : 'Form (last 5)'}</p>
        {stats.form.length === 0 ? (
          <p className="text-sm text-slate-500">{isItalian ? 'Nessuna partita giocata' : 'No matches played'}</p>
        ) : (
          <div className="flex gap-2 flex-wrap">
            {stats.form.map((f, i) => (
              <div key={i} title={`${f.home ? 'vs' : '@'} ${f.opponent} (${f.score})`}
                className={`w-9 h-9 border rounded flex items-center justify-center font-bold text-sm ${RES_STYLE[f.result]}`}>
                {isItalian ? resLabel[f.result] : f.result}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Records */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Record icon={Home} label={isItalian ? 'In Casa' : 'Home'} rec={stats.home_record} />
        <Record icon={Plane} label={isItalian ? 'In Trasferta' : 'Away'} rec={stats.away_record} />
        <div className="bg-[#050508] border border-white/10 p-3 flex items-center gap-3">
          <ShieldCheck size={18} className="text-[#00F0FF]" />
          <div>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Clean Sheets</p>
            <p className="font-mono text-sm text-white">{stats.clean_sheets}</p>
          </div>
        </div>
        <div className="bg-[#050508] border border-white/10 p-3 flex items-center gap-3">
          <Goal size={18} className="text-[#FFEA00]" />
          <div>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">{isItalian ? 'Media Gol' : 'Avg Goals'}</p>
            <p className="font-mono text-sm text-white">{stats.avg_goals_for} <span className="text-slate-500">/</span> <span className="text-[#FF1744]">{stats.avg_goals_against}</span></p>
          </div>
        </div>
      </div>

      {/* Player performance */}
      {stats.players.length > 0 && (
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">{isItalian ? 'Rendimento Giocatori' : 'Player Performance'}</p>
          <div className="space-y-1">
            {stats.players.slice(0, 10).map((p, i) => (
              <div key={p.player_id} className="flex items-center gap-3 bg-[#050508] border border-white/5 px-3 py-2 text-sm">
                <span className="font-mono text-slate-500 w-5">{i + 1}</span>
                <span className="text-white flex-1 truncate">{p.name}</span>
                <span className="font-mono text-[#FFEA00]">{p.goals} gol</span>
                <span className="font-mono text-[#00F0FF]">{p.assists} assist</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};
